import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import kineLogo from './kine_logo.png';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import UserProfile from './components/UserProfile/UserProfile';
import { saveUserToLocalStorage, getUserFromLocalStorage, getUserFromFirestore, updateUserInvoiceNumber, saveInvoiceToFirestore, updateInvoiceInFirestore } from './userService';
import invoiceTemplate from './invoiceTemplate';
import html2pdf from 'html3pdf';
import InvoicesPage from './components/InvoicesPage/InvoicesPage';
import { useLocation } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const TextInput = ({ fieldName, fieldData, handleInvoiceInputChange, getInvoicePlaceholder }) => {
  const inputRef = useRef(null);
  const [cursorPosition, setCursorPosition] = useState(null);

  useEffect(() => {
    if (inputRef.current && cursorPosition !== null) {
      inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
    }
  }, [fieldData.default, cursorPosition]);

  return (
    <input
      ref={inputRef}
      type="text"
      value={fieldData.default || ''}
      onChange={(e) => {
        const newCursorPosition = e.target.selectionStart;
        handleInvoiceInputChange(fieldName, e.target.value);
        setCursorPosition(newCursorPosition);
      }}
      onBlur={() => setCursorPosition(null)}
      placeholder={getInvoicePlaceholder(fieldName)}
      required={fieldData.required}
      className="form-input"
    />
  );
};

const TextareaInput = ({ fieldName, fieldData, handleInvoiceInputChange, getInvoicePlaceholder }) => {
  const textareaRef = useRef(null);
  const [cursorPosition, setCursorPosition] = useState(null);

  useEffect(() => {
    if (textareaRef.current && cursorPosition !== null) {
      textareaRef.current.setSelectionRange(cursorPosition, cursorPosition);
    }
  }, [fieldData.default, cursorPosition]);

  return (
    <textarea
      ref={textareaRef}
      value={fieldData.default || ''}
      onChange={(e) => {
        const newCursorPosition = e.target.selectionStart;
        handleInvoiceInputChange(fieldName, e.target.value);
        setCursorPosition(newCursorPosition);
      }}
      onBlur={() => setCursorPosition(null)}
      placeholder={getInvoicePlaceholder(fieldName)}
      required={fieldData.required}
      className="form-textarea"
    />
  );
};

function MainApp() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const initialInvoiceFormData = {
    company_information: { display: false, default: '', type: 'singleline_text', required: false, page: 0, section: 'GÉNÉRAL' },
    city: { default: 'Toulouse', type: 'singleline_text', required: false, page: 0, section: 'GÉNÉRAL' },
    date: { default: '', type: 'singleline_text', required: false, page: 0, section: 'GÉNÉRAL' },
    invoice_number: { default: '', type: 'singleline_text', required: false, page: 0, section: 'GÉNÉRAL' },
    customer_information: { display: false, default: '', type: 'singleline_text', required: false, page: 0, section: 'PATIENT' },
    customer_name: { default: '', type: 'singleline_text', required: false, page: 0, section: 'PATIENT' },
    customer_address: { default: '', type: 'singleline_text', required: false, page: 0, section: 'PATIENT' },
    customer_phone: { default: '', type: 'singleline_text', required: false, page: 0, section: 'PATIENT' },
    prescription_date: { default: '', type: 'singleline_text', required: false, page: 0, section: 'ORDONNANCE' },
    doctor_name: { default: '', type: 'singleline_text', required: false, page: 0, section: 'ORDONNANCE' },
    bilan_quantity: { default: '', type: 'singleline_text', required: false, page: 0, section: 'SÉANCES' },
    bilan_unit_price: { default: '', type: 'singleline_text', required: false, page: 0, section: 'SÉANCES' },
    session_quantity: { default: '', type: 'singleline_text', required: false, page: 0, section: 'SÉANCES' },
    session_unit_price: { default: '55', type: 'singleline_text', required: false, page: 0, section: 'SÉANCES' },
    session_dates: { default: '', type: 'multiline_text', required: false, page: 0, section: 'SÉANCES' },
    total_amount_ttc: { default: '', type: 'singleline_text', required: false, page: 0, section: 'PRIX' },
    payment_method: { display: false, default: '', type: 'singleline_text', required: false, page: 0, section: 'PRIX' },
    payment_type_check: { default: true, type: 'checkbox', required: false, page: 0, section: 'PRIX' },
    payment_type_cash: { default: false, type: 'checkbox', required: false, page: 0, section: 'PRIX' },
    payment_type_transfer: { default: false, type: 'checkbox', required: false, page: 0, section: 'PRIX' },
    refundable_treatments: { default: '', type: 'singleline_text', required: false, page: 0, section: 'PRIX' },
    cotation_acte: { default: '', type: 'singleline_text', required: false, page: 0, section: 'PRIX' },
    non_refundable_treatments: { default: '', type: 'singleline_text', required: false, page: 0, section: 'PRIX' }
  };
  const [invoiceFormData, setInvoiceFormData] = useState({
    company_information: { display: false, default: '', type: 'singleline_text', required: false, page: 0, section: 'GÉNÉRAL' },
    city: { default: 'Toulouse', type: 'singleline_text', required: false, page: 0, section: 'GÉNÉRAL' },
    date: { default: '', type: 'singleline_text', required: false, page: 0, section: 'GÉNÉRAL' },
    invoice_number: { default: '', type: 'singleline_text', required: false, page: 0, section: 'GÉNÉRAL' },
    customer_information: { display: false, default: '', type: 'singleline_text', required: false, page: 0, section: 'PATIENT' },
    customer_name: { default: '', type: 'singleline_text', required: false, page: 0, section: 'PATIENT' },
    customer_address: { default: '', type: 'singleline_text', required: false, page: 0, section: 'PATIENT' },
    customer_phone: { default: '', type: 'singleline_text', required: false, page: 0, section: 'PATIENT' },
    prescription_date: { default: '', type: 'singleline_text', required: false, page: 0, section: 'ORDONNANCE' },
    doctor_name: { default: '', type: 'singleline_text', required: false, page: 0, section: 'ORDONNANCE' },
    bilan_quantity: { default: '', type: 'singleline_text', required: false, page: 0, section: 'SÉANCES' },
    bilan_unit_price: { default: '', type: 'singleline_text', required: false, page: 0, section: 'SÉANCES' },
    session_quantity: { default: '', type: 'singleline_text', required: false, page: 0, section: 'SÉANCES' },
    session_unit_price: { default: '55', type: 'singleline_text', required: false, page: 0, section: 'SÉANCES' },
    session_dates: { default: '', type: 'multiline_text', required: false, page: 0, section: 'SÉANCES' },
    total_amount_ttc: { default: '', type: 'singleline_text', required: false, page: 0, section: 'PRIX' },
    payment_method: { display: false, default: '', type: 'singleline_text', required: false, page: 0, section: 'PRIX' },
    payment_type_check: { default: true, type: 'checkbox', required: false, page: 0, section: 'PRIX' },
    payment_type_cash: { default: false, type: 'checkbox', required: false, page: 0, section: 'PRIX' },
    payment_type_transfer: { default: false, type: 'checkbox', required: false, page: 0, section: 'PRIX' },
    refundable_treatments: { default: '', type: 'singleline_text', required: false, page: 0, section: 'PRIX' },
    cotation_acte: { default: '', type: 'singleline_text', required: false, page: 0, section: 'PRIX' },
    non_refundable_treatments: { default: '', type: 'singleline_text', required: false, page: 0, section: 'PRIX' }
  });

  const [lockFields, setLockFields] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [sections, setSections] = useState({});
  const [quoteSections, setQuoteSections] = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [navigation, setNavigation] = useState('invoice');
  const [editingInvoice, setEditingInvoice] = useState(null);

  useEffect(() => {
    //Reset invoiceFormData
    setInvoiceFormData(initialInvoiceFormData);
  }, []);

  //Fetch user and init date & invoice number
  useEffect(() => {
    //Do not init if coming from InvoicesPage to edit invoice
    if (user && navigation === 'invoice' && editingInvoice == null) {
      const initUserDataInvoiceNumberAndDate = async () => {
        //Init user data and invoice number
        const userFromFirestore = await getUserFromFirestore(user.email);

        if (userFromFirestore) {
          saveUserToLocalStorage(userFromFirestore);

          setInvoiceFormData(prevData => ({
            ...prevData,
            invoice_number: {
              ...prevData.invoice_number,
              default: userFromFirestore.invoiceNumber.toString()
            }
          }));
        } else {
          console.log('No user data found in Firestore : ', user);
          logout();
        }

        //Init invoice date
        const now = new Date();
        const formattedDate = `${String(now.getDate()).padStart(2, '0')}/${String(now.getMonth() + 1).padStart(2, '0')}/${String(now.getFullYear())}`;

        setInvoiceFormData(prevData => ({
          ...prevData,
          date: {
            ...prevData.date,
            default: formattedDate
          }
        }));
      };
      initUserDataInvoiceNumberAndDate();
    }
  }, [user, logout, navigation]);

  //Create form sections
  useEffect(() => {
    const organizedSections = {};

    Object.entries(invoiceFormData).forEach(([fieldName, fieldData]) => {
      const { page = 0, section = 'Other' } = fieldData;

      if (!organizedSections[page]) {
        organizedSections[page] = {};
      }

      if (!organizedSections[page][section]) {
        organizedSections[page][section] = {};
      }

      organizedSections[page][section][fieldName] = fieldData;
    });

    setSections(organizedSections);
  }, [invoiceFormData]);

  // Initialize form with invoice data from location state
  useEffect(() => {
    if (editingInvoice && navigation === 'invoice') {
      const invoice = editingInvoice;
      const newFormData = {};

      for (const key in invoiceFormData) {
        newFormData[key] = { ...invoiceFormData[key], default: invoice[key] !== undefined ? invoice[key] : invoiceFormData[key].default };
      }

      setInvoiceFormData(newFormData);
    }
  }, [navigation]);

  const handleInvoiceInputChange = (fieldName, value) => {
    setInvoiceFormData(prevData => {
      const newData = { ...prevData };
      newData[fieldName] = {
        ...newData[fieldName],
        default: value
      };

      if (fieldName === 'customer_name' || fieldName === 'customer_address' || fieldName === 'customer_phone') {
        newData["customer_information"] = {
          ...newData["customer_information"],
          default: `${newData["customer_address"].default}<br />${newData["customer_phone"].default}`
        };
      }

      //Restrict payment type to only one
      // if (fieldName === 'payment_type_check') {
      //   newData.payment_type_cash = {
      //     ...newData.payment_type_cash,
      //     default: !value
      //   };

      //   newData.payment_type_transfer = {
      //     ...newData.payment_type_transfer,
      //     default: !value
      //   };
      // } else if (fieldName === 'payment_type_cash') {
      //   newData.payment_type_check = {
      //     ...newData.payment_type_check,
      //     default: !value
      //   };

      //   newData.payment_type_transfer = {
      //     ...newData.payment_type_transfer,
      //     default: !value
      //   };
      // } else if (fieldName === 'payment_type_transfer') {
      //   newData.payment_type_check = {
      //     ...newData.payment_type_check,
      //     default: !value
      //   };

      //   newData.payment_type_cash = {
      //     ...newData.payment_type_cash,
      //     default: !value
      //   };
      // }

      return newData;
    });
  };

  const generateInvoiceHTML = () => {
    let htmlContent = invoiceTemplate;

    Object.entries(invoiceFormData).forEach(([fieldName, fieldData]) => {
      var value;

      if (fieldName === 'company_information') {
        const user = getUserFromLocalStorage();

        // Name
        htmlContent = htmlContent.replace(`{{company_name}}`, user.name);

        // Job title
        htmlContent = htmlContent.replace(`{{job_title}}`, user.jobTitle);

        // Address & phone
        if (user) {
          value = `${user.jobTitle}<br />${user.address}<br />${user.phoneNumber}`;
        }
      } else if (fieldName === 'total_amount_ttc' || fieldName === 'bilan_unit_price' || fieldName === 'session_unit_price' || fieldName === 'refundable_treatments' || fieldName === 'non_refundable_treatments') {
        value = (fieldData.default?.toString() || '0') + "€";
      } else if (fieldName === 'bilan_quantity' || fieldName === 'session_quantity') {
        value = fieldData.default?.toString() || '0';
      } else if (fieldName === 'payment_method') {
        if (invoiceFormData.payment_type_check.default) {
          value = 'chèque';
        } else if (invoiceFormData.payment_type_cash.default) {
          value = 'espèces';
        } else if (invoiceFormData.payment_type_transfer.default) {
          value = 'virement';
        }
      } else {
        value = fieldData.default?.toString() || '';
      }

      htmlContent = htmlContent.replaceAll(`{{${fieldName}}}`, value);
    });

    return htmlContent;
  };

  const downloadInvoiceHTML = () => {
    const htmlContent = generateInvoiceHTML();
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = invoiceFormData.customer_name.default.length > 0 ? 'Facture - ' + invoiceFormData.customer_name.default + '.html' : 'Facture.html';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadInvoicePDF = async () => {
    /*
      Download PDF
    */
    const htmlContent = generateInvoiceHTML();
    const element = document.createElement('div');
    element.innerHTML = htmlContent;

    const opt = {
      filename: invoiceFormData.customer_name.default.length > 0 ? 'Facture - ' + invoiceFormData.customer_name.default + ' - n°' + invoiceFormData.invoice_number.default + '.pdf' : 'Facture' + ' - n°' + invoiceFormData.invoice_number.default + '.pdf',
      pagebreak: { mode: 'avoid-all' }
    };

    html2pdf().set(opt).from(element).save();

    handleSaveInvoice();
  };

  const handleInvoicePreview = () => {
    const htmlContent = generateInvoiceHTML();
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    setPreviewUrl(url);
    setShowPreview(true);
  };

  const closePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setShowPreview(false);
  };

  const getInvoicePlaceholder = (fieldName) => {
    const placeholders = {
      city: "Fait à",
      date: "Fait le",
      invoice_number: "N° de facture",
      customer_name: "Nom du patient",
      customer_address: "Adresse du patient",
      customer_phone: "Téléphone du patient",
      prescription_date: "Date de l'ordonnance",
      doctor_name: "Nom du docteur",
      bilan_quantity: "Nombre de bilan diagnostique kinésithérapique",
      bilan_unit_price: "Prix unitaire du bilan diagnostique kinésithérapique",
      session_quantity: "Nombre de séances",
      session_unit_price: "Prix unitaire d'une séance",
      session_dates: "Dates des séances",
      total_amount_ttc: "Montant total TTC",
      payment_type_check: "Paiement par chèque",
      payment_type_cash: "Paiement en espèces",
      payment_type_transfer: "Paiement par virement",
      refundable_treatments: "Montant des traitements remboursables",
      cotation_acte: "Cotation des actes",
      non_refundable_treatments: "Montant des traitements non remboursables"
    };

    return placeholders[fieldName] || fieldName.replace(/_/g, ' ');
  };

  const renderInvoiceFieldLabel = (fieldName) => {
    const labels = {
      city: "Fait à",
      date: "Fait le",
      invoice_number: "N° de facture",
      customer_name: "Nom du patient",
      customer_address: "Adresse du patient",
      customer_phone: "Téléphone du patient",
      prescription_date: "Date de l'ordonnance",
      doctor_name: "Nom du docteur",
      bilan_quantity: "Nombre de bilan diagnostique kinésithérapique",
      bilan_unit_price: "Prix unitaire du bilan diagnostique kinésithérapique",
      session_quantity: "Nombre de séances",
      session_unit_price: "Prix unitaire d'une séance",
      session_dates: "Dates des séances",
      total_amount_ttc: "Montant total TTC",
      payment_type_check: "Paiement par chèque",
      payment_type_cash: "Paiement en espèces",
      payment_type_transfer: "Paiement par virement",
      refundable_treatments: "Montant des traitements remboursables",
      cotation_acte: "Cotation des actes",
      non_refundable_treatments: "Montant des traitements non remboursables"
    };

    return labels[fieldName] || fieldName.replace(/_/g, ' ');
  };

  const renderInvoiceField = (fieldName, fieldData) => {
    switch (fieldData.type) {
      case 'multiline_text':
        return (
          <TextareaInput
            fieldName={fieldName}
            fieldData={fieldData}
            handleInvoiceInputChange={handleInvoiceInputChange}
            getInvoicePlaceholder={getInvoicePlaceholder}
          />
        );
      case 'checkbox':
        return (
          <input
            type="checkbox"
            checked={fieldData.default || false}
            onChange={(e) => handleInvoiceInputChange(fieldName, e.target.checked)}
            required={fieldData.required}
            className="form-checkbox"
          />
        );
      default:
        return (
          <TextInput
            fieldName={fieldName}
            fieldData={fieldData}
            handleInvoiceInputChange={handleInvoiceInputChange}
            getInvoicePlaceholder={getInvoicePlaceholder}
          />
        );
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const handleSaveInvoice = async () => {
    const user = getUserFromLocalStorage();
    if (user) {
      /*
        Add invoice in Firestore
      */
      if (window.confirm('Voulez-vous sauvegarder cette facture ?')) {
        try {
          const invoiceData = { ...invoiceFormData }; // Create a copy
          for (const key in invoiceData) {
            invoiceData[key] = invoiceData[key].default; // Extract default values
          }

          if (editingInvoice) {
            await updateInvoiceInFirestore(editingInvoice.id, invoiceData);
          } else {
            await saveInvoiceToFirestore(invoiceData, user.id);
          }

          toast.success('La facture a bien été sauvegardée');
        } catch (error) {
          console.error('Error saving invoice data:', error);
          toast.error('Une erreur est survenue lors de la sauvegarde de la facture');
        }


        /*
          Update invoice number in Firestore only if it's a new invoice
        */
        if (!editingInvoice) {
          const newInvoiceNumber = user.invoiceNumber + 1;

          await updateUserInvoiceNumber(user, newInvoiceNumber);

          setInvoiceFormData(prevData => ({
            ...prevData,
            invoice_number: {
              ...prevData.invoice_number,
              default: newInvoiceNumber.toString()
            }
          }));
        }

        /*
          Reset invoice form data
        */
        handleReset();
      }
    }
  };

  const handleReset = async () => {
    // Reset editingInvoice & invoiceFormData
    setEditingInvoice(null);
    setInvoiceFormData(initialInvoiceFormData);

    // Initialize date & invoice number
    const userFromFirestore = await getUserFromFirestore(user.email);

    const now = new Date();
    const formattedDate = `${String(now.getDate()).padStart(2, '0')}/${String(now.getMonth() + 1).padStart(2, '0')}/${String(now.getFullYear())}`;

    setInvoiceFormData(prevData => ({
      ...prevData,
      date: {
        ...prevData.date,
        default: formattedDate
      },
      invoice_number: {
        ...prevData.invoice_number,
        default: userFromFirestore.invoiceNumber.toString()
      }
    }));
  };

  return (
    <div className="App">
      <ToastContainer />
      <header className="App-header">
        <img src={kineLogo} alt="Logo Masseur-Kinésithérapeute" className="logo" />
        {!user && (
          <h1>Isabelle BASSEAU MOMPART</h1>
        )}
        {user && (
          <h1>{navigation === 'invoice' ? 'Facture' : navigation === 'quote' ? 'Devis' : navigation === 'user_profile' ? 'Mon cabinet' : ''}</h1>
        )}
        {user && (
          <button onClick={handleLogout} className="logout-button">
            Se déconnecter
          </button>
        )}
      </header>
      {user ? (
        <div className="app-container">
          <nav className="side-menu">
            <button
              className={`menu-item ${navigation === 'invoice' ? 'active' : ''}`}
              onClick={() => {
                setNavigation('invoice');
              }}
            >
              Créer une Facture
            </button>
            <button
              className={`menu-item ${navigation === 'quote' ? 'active' : ''}`}
              onClick={() => setNavigation('quote')}
            >
              Créer un Devis
            </button>
            <button
              className={`menu-item ${navigation === 'invoices' ? 'active' : ''}`}
              onClick={() => setNavigation('invoices')}
            >
              Liste des Factures
            </button>
            <button
              className={`menu-item ${navigation === 'user_profile' ? 'active' : ''}`}
              onClick={() => setNavigation('user_profile')}
            >
              Mon cabinet
            </button>
          </nav>
          <main className="App-main">
            {navigation === 'invoice' ? (
              <>
                <div className="file-controls">
                  <div className="controls-container">
                    <button onClick={handleSaveInvoice} className="saveInvoice-button">
                      Sauvegarder
                    </button>
                    <button onClick={handleReset} className="reset-button">
                      Réinitialiser
                    </button>
                    <button onClick={handleInvoicePreview} className="preview-button">
                      Prévisualiser le PDF
                    </button>
                    <button onClick={downloadInvoicePDF} className="download-button">
                      Telecharger le PDF
                    </button>
                  </div>
                </div>

                {showPreview && (
                  <div className="preview-modal" onClick={closePreview}>
                    <div className="preview-content" onClick={(e) => e.stopPropagation()}>
                      <button onClick={closePreview} className="close-preview">
                        ×
                      </button>
                      <iframe
                        src={previewUrl}
                        title="HTML Preview"
                        width="100%"
                        height="100%"
                        style={{ border: 'none' }}
                      />
                    </div>
                  </div>
                )}
                <div className="form-container">

                  {sections.length > 1 && (
                    <div className="tabs">
                      {Object.keys(sections).map((page) => (
                        <button
                          key={page}
                          className={`tab ${currentPage === Number(page) ? 'active' : ''}`}
                          onClick={() => setCurrentPage(Number(page))}
                        >
                          Page {Number(page) + 1}
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="form-content">
                    {sections[currentPage] && Object.entries(sections[currentPage]).map(([section, fields]) => (
                      <div key={section} className="form-section">
                        <h3>{section}</h3>
                        <div className="fields-grid">
                          {Object.entries(fields).map(([fieldName, fieldData]) => (
                            (fieldData.display ?? true) && (
                              <div key={fieldName} className="field">
                                <label>
                                  {renderInvoiceFieldLabel(fieldName)}
                                  {fieldData.required && <span className="required">*</span>}
                                </label>
                                {renderInvoiceField(fieldName, fieldData)}
                              </div>
                            )
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {sections.length > 1 && (
                    <div className="navigation-buttons">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                        disabled={currentPage === 0}
                        className="nav-button"
                      >
                        Précédent
                      </button>
                      {currentPage !== Math.max(...Object.keys(sections).map(Number)) && (
                        <button
                          onClick={() => setCurrentPage(prev => Math.min(Math.max(...Object.keys(sections).map(Number)), prev + 1))}
                          disabled={currentPage === Math.max(...Object.keys(sections).map(Number))}
                          className="nav-button"
                        >
                          Suivant
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </>
            ) : navigation === 'quote' ? (
              <div>
                <div className="file-controls">
                  <div className="controls-container">
                    <div className="lock-controls">
                      <label className="lock-label">
                        <input
                          type="checkbox"
                          checked={lockFields}
                          onChange={(e) => setLockFields(e.target.checked)}
                        />
                        <span>Verrouiller les champs après remplissage</span>
                      </label>
                    </div>
                    <div className="button-group">
                      <button onClick={() => { }} className="preview-button">
                        Prévisualiser le PDF
                      </button>
                      <button onClick={() => { }} className="fill-button">
                        Télécharger le PDF
                      </button>
                    </div>
                  </div>
                </div>
                {showPreview && (
                  <div className="preview-modal" onClick={closePreview}>
                    <div className="preview-content" onClick={(e) => e.stopPropagation()}>
                      <button onClick={closePreview} className="close-preview">
                        ×
                      </button>
                      <iframe
                        src={previewUrl}
                        title="PDF Preview"
                        width="100%"
                        height="100%"
                        style={{ border: 'none' }}
                      />
                    </div>
                  </div>
                )}
                <div className="form-container">
                  <div className="form-content">
                    {/* {Object.entries(quoteSections).map(([section, fields]) => (
                          <div key={section} className="form-section">
                            <h3>{section}</h3>
                            <div className="fields-grid">
                              {Object.entries(fields).map(([fieldName, fieldData]) => (
                                <div key={fieldName} className="field">
                                  <label>
                                    {renderQuoteFieldLabel(fieldName)}
                                    {fieldData.required && <span className="required">*</span>}
                                  </label>
                                  {renderQuoteField(fieldName, fieldData)}
                                </div>
                              ))}
                            </div>
                          </div>
                        ))} */}
                  </div>
                </div>
              </div>
            ) : navigation === 'invoices' ? <InvoicesPage setNavigation={setNavigation} setEditingInvoice={setEditingInvoice} /> : navigation === 'user_profile' ? (
              <UserProfile />
            ) : null}
          </main>
        </div>
      ) : (
        <Login />
      )}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <MainApp />
    </AuthProvider>
  );
}

export default App;
